import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { type OptionItem, OptionType, useGetOptionsQuery } from "@/api/admin";
import { AdminSections } from "@/api/admin/teamRoles";
import type {
  AdminGroupedTransactionItem,
  AdminTransactionItem,
  AdminTransactionResponse,
} from "@/api/admin/transactions";
import { PaymentsMethods } from "@/api/restaurant/types/transactionsTypes";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownGroupBy,
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import { formatPaymentMethod } from "@/modules/RestaurantTransactions/formatPaymentMethod";
import { usePermission } from "@/services/casl/usePermission";
import {
  PartialState,
  refundCombinedStatusMap,
  STATUS_MAP_RUFUND_TRANSACTIONS,
  STATUS_MAP_TRANSACTIONS_COMBINED,
  STATUS_VARIANT_MAP_TRANSACTIONS,
  TransactionStatus,
  transactionStatusMap,
} from "@/types/api";
import { COMPANY_NAME } from "@/utils/constants/defaultNames";
import { formatEnumValue } from "@/utils/formatEnumValue";
import { formatFullName } from "@/utils/formatFullName";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useAdminMoney } from "@/utils/hooks/useAdminMoney";
import { useParamsFromUrl } from "@/utils/hooks/useParamsFromUrl";
import { type IGroupParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig, isOptionItem } from "../type";

enum TransactionsColumn {
  DATE_TIME = "Created at",
  REFERENCE_NUMBER = "Reference Number",
  ORDER_CODE = "Order code",
  DINER_NAME = "Diner name",
  NET_AMOUNT = "Net amount",
  GROSS_AMOUNT = "Gross amount",
  TAX_AMOUNT = "Taxes",
  TIPS = "Tips",
  TOTAL_PAYABLE = "Total payable",
  CREDIT_EARNED = "Credit earned",
  CREDIT_REDEEMED = "Credit redeemed",
  PAYMENT_METHOD = "Payment method",
  BRANCH = "Branch",
  BRAND = "Brand",
  GROUP = "Group",
  LEGAL_PAYABLE = "Payable Legal Entity",
  SETTLEMENT_STATUS = "Payment status",
  REFUND_STATUS = "Refund status",
}

const TABLE_HEADERS: TableHeaderType = Object.values(TransactionsColumn);

const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
  [TransactionsColumn.GROUP]: {
    type: "search",
    placeholder: "Search group",
    isSingleSelect: true,
  },
  [TransactionsColumn.BRAND]: {
    type: "search",
    placeholder: "Search brand",
    isSingleSelect: true,
  },
  [TransactionsColumn.BRANCH]: {
    type: "search",
    placeholder: "Search branch",
    isSingleSelect: true,
  },
  [TransactionsColumn.LEGAL_PAYABLE]: {
    type: "search",
    placeholder: "Search payable legal entity",
    isSingleSelect: true,
  },
  Status: { type: "status" },
  [TransactionsColumn.REFUND_STATUS]: { type: "status" },
};

enum GroupedTransactionsColumn {
  NAME = "Name",
  TOTAL_AMOUNT = "Total Gross Amount",
  NET_AMOUNT = "Total Net amount",
  TAX_AMOUNT = "Total Taxes",
  TIPS_AMOUNT = "Total Tips",
  TOTAL_PAYABLE = "Total Payable",
}

const GROUPED_TABLE_HEADERS: TableHeaderType = Object.values(
  GroupedTransactionsColumn
);

const reverseGroupByMap: Record<string, string> = {
  group: TransactionsColumn.GROUP,
  brand: TransactionsColumn.BRAND,
  branch: TransactionsColumn.BRANCH,
  legalEntity: TransactionsColumn.LEGAL_PAYABLE,
};

const groupByMap: Record<string, string> = {
  [TransactionsColumn.GROUP]: "group",
  [TransactionsColumn.BRAND]: "brand",
  [TransactionsColumn.BRANCH]: "branch",
  [TransactionsColumn.LEGAL_PAYABLE]: "legalEntity",
};

const groupByLabelMap: Record<string, TransactionsColumn> = {
  [TransactionsColumn.GROUP]: TransactionsColumn.GROUP,
  [TransactionsColumn.BRAND]: TransactionsColumn.BRAND,
  [TransactionsColumn.BRANCH]: TransactionsColumn.BRANCH,
  [TransactionsColumn.LEGAL_PAYABLE]: TransactionsColumn.LEGAL_PAYABLE,
};

interface TransactionsTableProps {
  tableData?: AdminTransactionResponse;
  tableParams: IGroupParams;
  setTableParams: React.Dispatch<React.SetStateAction<IGroupParams>>;
  handleNext: () => void;
  handlePrev: () => void;
  searchValue: string;
  setSearchValue: (val: string) => void;
  isPending: boolean;
}

const AdminTransactionsTable: FC<TransactionsTableProps> = ({
  tableData,
  setTableParams,
  handleNext,
  handlePrev,
  searchValue,
  setSearchValue,
  isPending,
}) => {
  const hasTransactionPermission = usePermission(AdminSections.TRANSACTION);

  const urlParamsForInit = useParamsFromUrl(setTableParams, [
    "brandId",
    "groupId",
    "branchId",
    "legalEntityId",
    "status",
    "refundPaymentStatus",
    "isPartial",
    "groupBy",
  ]);

  const [groupBy, setGroupBy] = useState<string>(() => {
    const fromUrl = new URLSearchParams(location.search).get("groupBy");
    if (fromUrl && reverseGroupByMap[fromUrl]) {
      return reverseGroupByMap[fromUrl];
    }
    return "None";
  });
  const isGrouped = groupBy !== "None";

  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.Brands,
    OptionType.Branches,
    OptionType.LegalEntities,
    OptionType.Groups,
  ]);

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [TransactionsColumn.GROUP]: optionsData?.groups ?? [],
    [TransactionsColumn.BRAND]: optionsData?.brands ?? [],
    [TransactionsColumn.BRANCH]: optionsData?.branches ?? [],
    [TransactionsColumn.LEGAL_PAYABLE]: optionsData?.legalEntities ?? [],
    Status: Object.keys(transactionStatusMap),
    [TransactionsColumn.REFUND_STATUS]: Object.keys(refundCombinedStatusMap),
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const handleSetFilter = useCallback(
    (selectedMap: Record<string, string[] | OptionItem[]>) => {
      const rawBranch = selectedMap[TransactionsColumn.BRANCH]?.[0];
      const brandId = selectedMap[TransactionsColumn.BRAND]?.[0];
      const rawGroup = selectedMap[TransactionsColumn.GROUP]?.[0];
      const rawLegal = selectedMap[TransactionsColumn.LEGAL_PAYABLE]?.[0];

      const selectedStatuses =
        (selectedMap["Status"] as string[] | undefined) ?? [];

      const hasPartial = selectedStatuses.includes(PartialState.partial);
      const hasFull = selectedStatuses.includes(PartialState.full);

      const rawStatuses = selectedStatuses
        .filter(
          status =>
            status !== PartialState.partial && status !== PartialState.full
        )
        .map(status => transactionStatusMap[status])
        .filter(Boolean);

      if (hasPartial || hasFull) {
        rawStatuses.push(TransactionStatus.COMPLETED);
      }

      let isPartial: boolean | undefined;

      if (hasPartial && !hasFull) {
        isPartial = true;
      } else if (!hasPartial && hasFull) {
        isPartial = false;
      } else {
        isPartial = undefined;
      }

      const selectedRefundStatuses =
        (selectedMap[TransactionsColumn.REFUND_STATUS] as
          | string[]
          | undefined) ?? [];

      const rawRefundStatuses = selectedRefundStatuses.map(
        status => refundCombinedStatusMap[status]
      );

      setTableParams(prev => {
        const newGroupId = isOptionItem(rawGroup) ? rawGroup.id : undefined;
        const newBrandId = isOptionItem(brandId) ? brandId.id : undefined;
        const newBranchId = isOptionItem(rawBranch) ? rawBranch.id : undefined;
        const newLegalEntityId = isOptionItem(rawLegal)
          ? rawLegal.id
          : undefined;
        const newStatus = rawStatuses.length
          ? [...new Set(rawStatuses)]
          : undefined;
        const newRefundPaymentStatus = rawRefundStatuses.length
          ? rawRefundStatuses
          : undefined;

        if (
          prev.groupId === newGroupId &&
          prev.brandId === newBrandId &&
          prev.branchId === newBranchId &&
          prev.legalEntityId === newLegalEntityId &&
          prev.isPartial === isPartial &&
          JSON.stringify(prev.status ?? null) ===
            JSON.stringify(newStatus ?? null) &&
          JSON.stringify(prev.refundPaymentStatus ?? null) ===
            JSON.stringify(newRefundPaymentStatus ?? null)
        ) {
          return prev;
        }

        return {
          ...prev,
          isPartial,
          status: newStatus,
          refundPaymentStatus: newRefundPaymentStatus,
          groupId: newGroupId,
          brandId: newBrandId,
          branchId: newBranchId,
          legalEntityId: newLegalEntityId,
          offset: 0,
        };
      });
    },
    [setTableParams]
  );

  const HIDDEN_BY_DEFAULT = new Set([
    TransactionsColumn.REFERENCE_NUMBER,
    TransactionsColumn.NET_AMOUNT,
    TransactionsColumn.TOTAL_PAYABLE,
    TransactionsColumn.CREDIT_EARNED,
    TransactionsColumn.CREDIT_REDEEMED,
    TransactionsColumn.LEGAL_PAYABLE,
  ]);

  const { visibleColumns, setVisibleColumns, flags } = useVisibleFlags(
    TABLE_HEADERS,
    HIDDEN_BY_DEFAULT,
    "admin_transactions_visible_columns"
  );

  const {
    visibleColumns: groupedVisibleColumns,
    setVisibleColumns: setGroupedVisibleColumns,
    flags: groupedFlags,
  } = useVisibleFlags(
    GROUPED_TABLE_HEADERS,
    undefined,
    "admin_transactions_grouped_visible_columns"
  );

  const groupedNameLabel =
    groupBy !== "None" ? groupByLabelMap[groupBy] : TransactionsColumn.GROUP;

  useEffect(() => {
    const targetGroupBy = groupBy === "None" ? undefined : groupByMap[groupBy];
    setTableParams(prev => {
      if (prev.groupBy === targetGroupBy) return prev;
      return {
        ...prev,
        groupBy: targetGroupBy,
        offset: 0,
      };
    });
  }, [groupBy, setTableParams]);

  const handleGroupByChange = (val: string) => {
    setGroupBy(val);
    setTableParams(prev => {
      const newParams = { ...prev, offset: 0 };

      if (val === "None") {
        delete newParams.groupBy;
        return newParams;
      }

      return {
        ...newParams,
        groupBy: groupByMap[val],
      };
    });
  };

  const { initialMap, initialObjects } = useMemo(() => {
    const map: Record<string, string[]> = {};
    const objects: Record<string, OptionItem[]> = {};

    const findAndSetOption = (
      id: string | undefined,
      options: OptionItem[] | undefined,
      columnName: string
    ) => {
      if (id && options && options.length > 0) {
        const found = options.find(opt => opt.id === id);
        if (found) {
          map[columnName] = [found.name];
          objects[columnName] = [found];
        }
      }
    };

    findAndSetOption(
      urlParamsForInit?.brandId as string,
      optionsData?.brands,
      TransactionsColumn.BRAND
    );
    findAndSetOption(
      urlParamsForInit?.groupId as string,
      optionsData?.groups,
      TransactionsColumn.GROUP
    );
    findAndSetOption(
      urlParamsForInit?.branchId as string,
      optionsData?.branches,
      TransactionsColumn.BRANCH
    );
    findAndSetOption(
      urlParamsForInit?.legalEntityId as string,
      optionsData?.legalEntities,
      TransactionsColumn.LEGAL_PAYABLE
    );

    const statusArray: string[] = [];
    if (urlParamsForInit?.status) {
      const statuses = (urlParamsForInit.status as string).split(",");
      const reverseTxStatusMap = Object.entries(transactionStatusMap).reduce(
        (acc, [key, value]) => ({ ...acc, [value]: key }),
        {} as Record<string, string>
      );
      statuses.forEach(s => {
        if (reverseTxStatusMap[s]) statusArray.push(reverseTxStatusMap[s]);
      });
    }

    if (urlParamsForInit?.isPartial !== undefined) {
      if (String(urlParamsForInit.isPartial) === "true")
        statusArray.push(PartialState.partial);
      if (String(urlParamsForInit.isPartial) === "false")
        statusArray.push(PartialState.full);
    }

    if (statusArray.length > 0) {
      map["Status"] = statusArray;
    }

    if (urlParamsForInit?.refundPaymentStatus) {
      const refundStatuses = (
        urlParamsForInit.refundPaymentStatus as string
      ).split(",");
      const reverseRefundMap = Object.entries(refundCombinedStatusMap).reduce(
        (acc, [key, value]) => ({ ...acc, [value]: key }),
        {} as Record<string, string>
      );
      const refundArr = refundStatuses
        .map(s => reverseRefundMap[s])
        .filter(Boolean);
      if (refundArr.length > 0) {
        map[TransactionsColumn.REFUND_STATUS] = refundArr;
      }
    }

    return { initialMap: map, initialObjects: objects };
  }, [optionsData, urlParamsForInit]);

  const money = useAdminMoney();

  const columns: ColumnConfig<AdminTransactionItem>[] = [
    {
      key: "createdAt",
      label: TransactionsColumn.DATE_TIME,
      visible: flags[TransactionsColumn.DATE_TIME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.createdAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "referenceNumber",
      label: TransactionsColumn.REFERENCE_NUMBER,
      visible: flags[TransactionsColumn.REFERENCE_NUMBER],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.referenceNumber.toString()}
        </TableCellText>
      ),
    },
    {
      key: "orderCode",
      label: TransactionsColumn.ORDER_CODE,
      visible: flags[TransactionsColumn.ORDER_CODE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        const code = row?.orderCode ?? "-";
        return hasTransactionPermission ? (
          <TableCellLink last={isLast}>
            <Link
              to={PATHS.ADMIN.SYSTEMS_TRANSACTIONS_DETAILS(row.id)}
              state={{
                from: `${window.location.pathname}${window.location.search}`,
              }}
              data-testid={`cell-id-${row.id}`}
            >
              {code}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{code}</TableCellText>
        );
      },
    },
    {
      key: "diner",
      label: TransactionsColumn.DINER_NAME,
      visible: flags[TransactionsColumn.DINER_NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatFullName(row?.diner?.firstName, row?.diner?.lastName)}
        </TableCellText>
      ),
    },
    {
      key: "netAmount",
      label: TransactionsColumn.NET_AMOUNT,
      visible: flags[TransactionsColumn.NET_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.netAmount?.toString())}
        </TableCellText>
      ),
    },
    {
      key: "grossAmount",
      label: TransactionsColumn.GROSS_AMOUNT,
      visible: flags[TransactionsColumn.GROSS_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row.grossAmount.toString())}
        </TableCellText>
      ),
    },
    {
      key: "taxAmount",
      label: TransactionsColumn.TAX_AMOUNT,
      visible: flags[TransactionsColumn.TAX_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row.taxAmount.toString())}
        </TableCellText>
      ),
    },
    {
      key: "tipsAmount",
      label: TransactionsColumn.TIPS,
      visible: flags[TransactionsColumn.TIPS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row.tipsAmount.toString())}
        </TableCellText>
      ),
    },
    {
      key: "totalPayable",
      label: TransactionsColumn.TOTAL_PAYABLE,
      visible: flags[TransactionsColumn.TOTAL_PAYABLE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.totalPayable?.toString() ?? "-")}
        </TableCellText>
      ),
    },
    {
      key: "cashbackEarned",
      label: TransactionsColumn.CREDIT_EARNED,
      visible: flags[TransactionsColumn.CREDIT_EARNED],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.cashbackEarned)}
        </TableCellText>
      ),
    },
    {
      key: "creditRedeemed",
      label: TransactionsColumn.CREDIT_REDEEMED,
      visible: flags[TransactionsColumn.CREDIT_REDEEMED],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.creditRedeemed)}
        </TableCellText>
      ),
    },
    {
      key: "paymentMethod",
      label: TransactionsColumn.PAYMENT_METHOD,
      visible: flags[TransactionsColumn.PAYMENT_METHOD],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          <>
            {row?.usedWallet &&
            row?.paymentMethod !== PaymentsMethods.SELFSERVE_WALLET
              ? `${COMPANY_NAME} Wallet + `
              : ""}
            {formatPaymentMethod(row?.paymentMethod ?? "-")}
          </>
        </TableCellText>
      ),
    },
    {
      key: "branchName",
      label: TransactionsColumn.BRANCH,
      visible: flags[TransactionsColumn.BRANCH],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes="w-[9%]">
          {row.branchName}
        </TableCellText>
      ),
    },
    {
      key: "brandName",
      label: TransactionsColumn.BRAND,
      visible: flags[TransactionsColumn.BRAND],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes="w-[9%]">
          {row.brandName}
        </TableCellText>
      ),
    },
    {
      key: "groupName",
      label: TransactionsColumn.GROUP,
      visible: flags[TransactionsColumn.GROUP],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes="w-[9%]">
          {row.groupName}
        </TableCellText>
      ),
    },
    {
      key: "legalEntityName",
      label: TransactionsColumn.LEGAL_PAYABLE,
      visible: flags[TransactionsColumn.LEGAL_PAYABLE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes="w-[9%]">
          {row.legalEntityName ?? "-"}
        </TableCellText>
      ),
    },
    {
      key: "status",
      label: TransactionsColumn.SETTLEMENT_STATUS,
      visible: flags[TransactionsColumn.SETTLEMENT_STATUS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        if (!row?.status) {
          return <TableCellText last={isLast}> - </TableCellText>;
        }

        return (
          <TableCellBadge
            last={isLast}
            classes="text-center"
            stage={STATUS_VARIANT_MAP_TRANSACTIONS[row.status]?.badge}
          >
            {formatEnumValue(row?.status)}
          </TableCellBadge>
        );
      },
    },
    {
      key: "paymentRefundStatus",
      label: TransactionsColumn.REFUND_STATUS,
      visible: flags[TransactionsColumn.REFUND_STATUS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        if (!row?.paymentRefundStatus) {
          return (
            <TableCellText last={isLast} classes="text-center">
              -
            </TableCellText>
          );
        }
        return (
          <TableCellBadge
            last={isLast}
            classes="text-center"
            stage={
              STATUS_MAP_RUFUND_TRANSACTIONS[row?.paymentRefundStatus]?.badge
            }
          >
            {formatEnumValue(row?.paymentRefundStatus)}
          </TableCellBadge>
        );
      },
    },
  ];

  const groupedColumns: ColumnConfig<AdminGroupedTransactionItem>[] = [
    {
      key: "groupName",
      label: groupedNameLabel,
      visible: groupedFlags[GroupedTransactionsColumn.NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row?.groupName ?? "-"}</TableCellText>
      ),
    },
    {
      key: "totalAmount",
      label: GroupedTransactionsColumn.TOTAL_AMOUNT,
      visible: groupedFlags[GroupedTransactionsColumn.TOTAL_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.totalAmount?.toString())}
        </TableCellText>
      ),
    },
    {
      key: "netAmount",
      label: GroupedTransactionsColumn.NET_AMOUNT,
      visible: groupedFlags[GroupedTransactionsColumn.NET_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.netAmount?.toString())}
        </TableCellText>
      ),
    },
    {
      key: "taxAmount",
      label: GroupedTransactionsColumn.TAX_AMOUNT,
      visible: groupedFlags[GroupedTransactionsColumn.TAX_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.taxAmount?.toString())}
        </TableCellText>
      ),
    },
    {
      key: "tipsAmount",
      label: GroupedTransactionsColumn.TIPS_AMOUNT,
      visible: groupedFlags[GroupedTransactionsColumn.TIPS_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.tipsAmount?.toString())}
        </TableCellText>
      ),
    },
    {
      key: "totalPayable",
      label: GroupedTransactionsColumn.TOTAL_PAYABLE,
      visible: groupedFlags[GroupedTransactionsColumn.TOTAL_PAYABLE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {money.render(row?.totalPayable?.toString())}
        </TableCellText>
      ),
    },
  ];

  const listGroupBy = [
    "None",
    TransactionsColumn.GROUP,
    TransactionsColumn.BRAND,
    TransactionsColumn.BRANCH,
    TransactionsColumn.LEGAL_PAYABLE,
  ];

  const dynamicGroupedHeaders = [
    groupBy !== "None" ? groupByLabelMap[groupBy] : TransactionsColumn.GROUP,
    ...Object.values(GroupedTransactionsColumn).filter(
      col => col !== GroupedTransactionsColumn.NAME
    ),
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={STATUS_MAP_TRANSACTIONS_COMBINED}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
        initialSelectedMap={initialMap as Record<string, string[]>}
        initialSelectedObjectMap={
          initialObjects as Record<string, OptionItem[]>
        }
      >
        <DropDownViewMenu
          viewList={isGrouped ? dynamicGroupedHeaders : TABLE_HEADERS}
          visibleColumns={isGrouped ? groupedVisibleColumns : visibleColumns}
          setVisibleColumns={
            isGrouped ? setGroupedVisibleColumns : setVisibleColumns
          }
        />
        <DropDownGroupBy
          selectedItem={groupBy}
          setSelectedItem={handleGroupByChange}
          viewList={listGroupBy}
        />
      </FilterSearch>

      {isPending || isOptionPending ? (
        <Loader />
      ) : isGrouped ? (
        <DynamicTable<AdminGroupedTransactionItem>
          data={tableData?.items || []}
          columns={groupedColumns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={tableData}
          setTableParams={setTableParams}
        />
      ) : (
        <DynamicTable<AdminTransactionItem>
          data={tableData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={tableData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AdminTransactionsTable;
